import { DataProvider } from './DataProvider'
import { Task, TaskFilter, TaskUpdate } from '@/lib/types'
import { supabase } from '@/lib/supabase'
import { activityLogger } from '@/lib/activity-logger'
import { openExternalUrl } from '@/lib/window-utils'
import {
  parseRepeatPattern,
  parseRepeatId,
  calculateNextDate,
  generateRepeatId,
  getTodayString,
} from '@/lib/repeat-utils'
import {
  updateRoamTaskDate,
  toggleRoamTaskComplete,
  updateCheckvistTaskDate,
  toggleCheckvistTaskComplete,
  updateCheckvistAgileStatus,
  type RoamCredentials,
  type CheckvistCredentials
} from '@/lib/sync-services'
import {
  toggleWorkflowyTaskComplete,
  updateWorkflowyTaskDate,
  updateWorkflowyTaskTime,
  updateWorkflowyTaskName,
  isWorkflowyApiAvailable
} from '@/lib/workflowy-api'
import type { TaskSidekickSettings } from '@/components/Settings'

/**
 * Supabase data provider for webapp
 * Directly accesses Supabase database
 */
export class SupabaseProvider implements DataProvider {
  private userId: string

  constructor(userId: string) {
    this.userId = userId
  }

  async getTasks(filter?: TaskFilter): Promise<Task[]> {
    console.log('[SupabaseProvider] getTasks called for user_id:', this.userId)

    // Use cursor-based pagination for better performance
    // Instead of OFFSET (which scans N rows), we use WHERE clause with last seen cursor
    const allTasks: any[] = []
    const batchSize = 1000
    let cursor: { created_at: string; id: string } | null = null
    let hasMore = true

    while (hasMore) {
      let query: any = supabase
        .from('tasks')
        .select('*', { count: cursor === null ? 'exact' : undefined })
        .eq('user_id', this.userId)

      // Apply cursor (only fetch rows older than cursor)
      if (cursor) {
        // Use composite cursor: created_at < X OR (created_at = X AND id < Y)
        // This ensures we get the next page correctly even with duplicate timestamps
        query = query.or(`created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.lt.${cursor.id})`)
      }

      if (filter?.source && filter.source !== 'all') {
        query = query.eq('source', filter.source)
      }

      if (filter?.status) {
        query = query.eq('status', filter.status)
      }

      const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .order('id', { ascending: false }) // Secondary sort for stable cursor
        .limit(batchSize)

      if (error) {
        console.error('[SupabaseProvider] Error fetching tasks:', error)
        break
      }

      if (data && data.length > 0) {
        allTasks.push(...data)

        // Update cursor to last item in batch
        const lastItem: any = data[data.length - 1]
        cursor = {
          created_at: lastItem.created_at,
          id: lastItem.id
        }

        console.log(`[SupabaseProvider] Batch loaded: ${data.length} tasks (cursor: ${cursor.created_at}, total so far: ${allTasks.length})`)
      }

      // Check if we've fetched all tasks
      hasMore = data && data.length === batchSize

      // Log total count on first batch
      if (cursor && count) {
        console.log(`[SupabaseProvider] Total tasks in database: ${count}`)
      }
    }

    console.log('[SupabaseProvider] Final result:', {
      loaded: allTasks.length,
      userId: this.userId
    })

    return allTasks.map(row => this.transformTask(row))
  }

  async getTask(taskId: string): Promise<Task | null> {
    const { data, error } = await supabase
      .from('nodes')
      .select('*')
      .eq('id', taskId)
      .eq('user_id', this.userId)
      .eq('type', 'task')
      .is('deleted_at', null)
      .single()

    if (error || !data) {
      return null
    }

    return this.transformTask(data)
  }

  async createTask(task: Partial<Task>): Promise<Task> {
    const { data, error } = await supabase
      .from('tasks')
      .insert({
        user_id: this.userId,
        name: task.name || 'Untitled',
        note: task.note || null,
        due_date: task.dueDate || null,
        due_time: task.dueTime || null,
        duration: task.duration || null,
        completed: task.completed || false,
        status: task.status || 'todo',
        priority: task.priority || 0,
        tags: task.tags || [],
        source: task.source || 'manual',
        source_url: task.sourceUrl || null,
        assigned_to: task.assignedTo || 'user', // Default to user
        metadata: task.metadata || {}, // Flexible JSONB metadata
        project: task.project || null,
        project_path: task.projectPath || null,
        attachments: task.attachments || [],
      })
      .select()
      .single()

    if (error) {
      throw new Error(`Failed to create task: ${error.message}`)
    }

    const createdTask = this.transformTask(data)

    // Log activity
    await activityLogger.log({
      userId: this.userId,
      actionType: 'task_created',
      entityType: 'task',
      entityId: createdTask.id,
      details: {
        name: createdTask.name,
        source: createdTask.source,
        priority: createdTask.priority,
      },
    })

    return createdTask
  }

  async updateTask(taskId: string, updates: TaskUpdate): Promise<boolean> {
    console.log('[SupabaseProvider] updateTask called:', { taskId, updates })

    // Get existing task to compare changes
    const existingTask = await this.getTask(taskId)
    console.log('[SupabaseProvider] Existing task fetched:', existingTask ? { id: existingTask.id, source: existingTask.source } : 'null')

    // Update Supabase first
    const { error } = await supabase
      .from('nodes')
      .update({
        content: updates.name,
        note: updates.note,
        due_date: updates.dueDate,
        due_time: updates.dueTime,
        duration: updates.duration,
        completed: updates.completed,
        status: updates.status,
        priority: updates.priority,
        tags: updates.tags,
        updated_at: new Date().toISOString(),
      })
      .eq('id', taskId)
      .eq('user_id', this.userId)

    if (error) {
      console.error('[SupabaseProvider] Update failed:', error)
      return false
    }

    console.log('[SupabaseProvider] Supabase update successful')

    // Push changes back to source (2-way sync)
    // BUT: Skip if user is disconnecting from source (changing to 'manual')
    if (updates.source === 'manual') {
      console.log('[SupabaseProvider] Task disconnected from source (now manual) - skipping 2-way sync')
    } else if (existingTask) {
      console.log('[SupabaseProvider] Calling syncChangesToSource...')
      await this.syncChangesToSource(existingTask, updates)
    } else {
      console.warn('[SupabaseProvider] Skipping syncChangesToSource - existingTask is null')
    }

    // Determine action type based on what changed
    let actionType: 'task_updated' | 'task_completed' | 'task_uncompleted' = 'task_updated'
    if (existingTask && updates.completed !== undefined) {
      if (updates.completed && !existingTask.completed) {
        actionType = 'task_completed'

        // Handle repeating tasks: create next instance when marking as done
        await this.handleRepeatTask(existingTask)
      } else if (!updates.completed && existingTask.completed) {
        actionType = 'task_uncompleted'
      }
    }

    // Log activity
    await activityLogger.log({
      userId: this.userId,
      actionType,
      entityType: 'task',
      entityId: taskId,
      details: {
        name: updates.name || existingTask?.name,
        changes: Object.keys(updates),
      },
    })

    return true
  }

  /**
   * Sync task changes back to original source (2-way sync)
   */
  private async syncChangesToSource(existingTask: Task, updates: TaskUpdate): Promise<void> {
    const taskId = existingTask.id
    const source = existingTask.source

    console.log('[SupabaseProvider] syncChangesToSource called:', { taskId, source, updates })

    // Only sync if this is a task from an external source
    // ID format: roam-{blockUid}
    if (source === 'roam' && taskId.startsWith('roam-')) {
      // Load settings
      const settings = await this.getSetting<TaskSidekickSettings>('task_sidekick_settings')
      if (!settings?.roamGraphName || !settings?.roamApiToken) {
        console.log('[SupabaseProvider] Roam not configured, skipping 2-way sync')
        return
      }

      const credentials: RoamCredentials = {
        graphName: settings.roamGraphName,
        apiToken: settings.roamApiToken
      }

      // Sync completion status
      if (updates.completed !== undefined && updates.completed !== existingTask.completed) {
        const success = await toggleRoamTaskComplete(taskId, updates.completed, credentials)
        if (!success) {
          console.error('[SupabaseProvider] Failed to sync completion to Roam')
        }
      }

      // Sync date changes
      if (updates.dueDate !== undefined && updates.dueDate !== existingTask.dueDate) {
        const success = await updateRoamTaskDate(taskId, updates.dueDate, credentials)
        if (!success) {
          console.error('[SupabaseProvider] Failed to sync date to Roam')
        }
      }

      console.log('[SupabaseProvider] Synced changes back to Roam:', taskId)

      // Trigger immediate refresh after 2-way sync
      console.log('[SupabaseProvider] Dispatching 2-way-sync event for immediate refresh')
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('task-sidekick:2way-sync'))
      }, 1000) // Wait 1s for Roam to process
    } else if (source === 'checkvist' && taskId.startsWith('cv-')) {
      // Load settings
      const settings = await this.getSetting<TaskSidekickSettings>('task_sidekick_settings')
      console.log('[SupabaseProvider] Loaded settings for Checkvist:', {
        hasSettings: !!settings,
        hasCheckvist: !!settings?.checkvist,
        username: settings?.checkvist?.username,
        hasApiToken: !!settings?.checkvist?.apiToken
      })
      if (!settings?.checkvist?.username || !settings?.checkvist?.apiToken) {
        console.log('[SupabaseProvider] Checkvist not configured, skipping 2-way sync')
        return
      }

      const credentials: CheckvistCredentials = {
        username: settings.checkvist.username,
        apiKey: settings.checkvist.apiToken
      }

      // Sync completion status
      if (updates.completed !== undefined && updates.completed !== existingTask.completed) {
        const success = await toggleCheckvistTaskComplete(taskId, updates.completed, credentials)
        if (!success) {
          console.error('[SupabaseProvider] Failed to sync completion to Checkvist')
        }
      }

      // Sync date changes
      if (updates.dueDate !== undefined && updates.dueDate !== existingTask.dueDate) {
        const success = await updateCheckvistTaskDate(taskId, updates.dueDate, credentials)
        if (!success) {
          console.error('[SupabaseProvider] Failed to sync date to Checkvist')
        }
      }

      // Sync agile status changes (now/next/later tags)
      if (updates.tags !== undefined && updates.tags !== existingTask.tags) {
        // Check if agile status changed
        const oldNow = existingTask.tags?.includes('now') || false
        const oldNext = existingTask.tags?.includes('next') || false
        const oldLater = existingTask.tags?.includes('later') || false
        const newNow = updates.tags?.includes('now') || false
        const newNext = updates.tags?.includes('next') || false
        const newLater = updates.tags?.includes('later') || false

        if (oldNow !== newNow || oldNext !== newNext || oldLater !== newLater) {
          let agileStatus: 'now' | 'next' | 'later' | null = null
          if (newNow) agileStatus = 'now'
          else if (newNext) agileStatus = 'next'
          else if (newLater) agileStatus = 'later'

          const success = await updateCheckvistAgileStatus(taskId, agileStatus, credentials)
          if (!success) {
            console.error('[SupabaseProvider] Failed to sync agile status to Checkvist')
          }
        }
      }

      console.log('[SupabaseProvider] Synced changes back to Checkvist:', taskId)

      // Trigger immediate refresh after 2-way sync
      console.log('[SupabaseProvider] Dispatching 2-way-sync event for immediate refresh')
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('task-sidekick:2way-sync'))
      }, 1000) // Wait 1s for Checkvist to process
    } else if (source === 'workflowy' && taskId.startsWith('wf-')) {
      const workflowyAvailable = await isWorkflowyApiAvailable()
      if (!workflowyAvailable) {
        console.log('[SupabaseProvider] Workflowy API not configured, skipping 2-way sync')
        return
      }

      // Sync completion status
      if (updates.completed !== undefined && updates.completed !== existingTask.completed) {
        await toggleWorkflowyTaskComplete(taskId, updates.completed)
      }

      // Sync date changes
      if (updates.dueDate !== undefined && updates.dueDate !== existingTask.dueDate) {
        await updateWorkflowyTaskDate(taskId, updates.dueDate)
      }

      // Sync time changes
      if (updates.dueTime !== undefined && updates.dueTime !== existingTask.dueTime) {
        await updateWorkflowyTaskTime(taskId, updates.dueTime)
      }

      // Sync name changes
      if (updates.name !== undefined && updates.name !== existingTask.name) {
        await updateWorkflowyTaskName(taskId, updates.name)
      }

      console.log('[SupabaseProvider] Synced changes back to Workflowy:', taskId)
    }
    // Obsidian: Already works via Obsidian plugin listening to Supabase changes
  }

  /**
   * Handle repeating tasks: create next instance when task is marked as done
   */
  private async handleRepeatTask(task: Task): Promise<void> {
    const pattern = parseRepeatPattern(task)
    if (!pattern) return // Not a repeating task

    console.log('[SupabaseProvider] Repeating task detected:', pattern)

    // Get current date from task or use today
    const currentDate = task.dueDate || getTodayString()
    const nextDate = calculateNextDate(currentDate, pattern)

    if (!nextDate) {
      console.error('[SupabaseProvider] Could not calculate next date for pattern:', pattern)
      return
    }

    // Get or generate repeat ID
    let repeatId = parseRepeatId(task)
    if (!repeatId) {
      repeatId = generateRepeatId()
      console.log('[SupabaseProvider] Generated new repeat ID:', repeatId)

      // Update original task with repeat ID
      await supabase
        .from('tasks')
        .update({
          repeat_id: repeatId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', task.id)
        .eq('user_id', this.userId)
    }

    // Create next instance
    const nextTask: Partial<Task> = {
      name: task.name,
      rawName: task.rawName,
      note: task.note,
      dueDate: nextDate,
      dueTime: task.dueTime,
      duration: task.duration,
      completed: false, // New instance starts as TODO
      status: 'todo',
      priority: task.priority,
      tags: task.tags,
      repeat: task.repeat,
      repeatId: repeatId,
      project: task.project,
      projectPath: task.projectPath,
      source: task.source,
      sourceUrl: task.sourceUrl,
    }

    const { data, error } = await supabase
      .from('tasks')
      .insert({
        user_id: this.userId,
        name: nextTask.name || 'Untitled',
        raw_name: nextTask.rawName,
        note: nextTask.note,
        due_date: nextTask.dueDate,
        due_time: nextTask.dueTime,
        duration: nextTask.duration,
        completed: false,
        status: 'todo',
        priority: nextTask.priority || 0,
        tags: nextTask.tags || [],
        repeat: nextTask.repeat,
        repeat_id: repeatId,
        project: nextTask.project,
        project_path: nextTask.projectPath,
        source: nextTask.source || 'obsidian',
        source_url: nextTask.sourceUrl,
      })
      .select()
      .single()

    if (error) {
      console.error('[SupabaseProvider] Error creating next repeat instance:', error)
      return
    }

    console.log('[SupabaseProvider] Created next repeat instance:', {
      id: data.id,
      dueDate: nextDate,
      repeatId,
    })

    // Log activity
    await activityLogger.log({
      userId: this.userId,
      actionType: 'task_created',
      entityType: 'task',
      entityId: data.id,
      details: {
        name: nextTask.name,
        source: nextTask.source,
        repeat: true,
        repeatId,
        previousTaskId: task.id,
      },
    })
  }

  async deleteTask(taskId: string): Promise<boolean> {
    // Get task details before deletion
    const task = await this.getTask(taskId)

    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId)
      .eq('user_id', this.userId)

    if (error) return false

    // Log activity
    if (task) {
      await activityLogger.log({
        userId: this.userId,
        actionType: 'task_deleted',
        entityType: 'task',
        entityId: taskId,
        details: {
          name: task.name,
          source: task.source,
        },
      })
    }

    return true
  }

  async navigateToTask(taskId: string): Promise<void> {
    console.log('[SupabaseProvider] navigateToTask called for taskId:', taskId)
    const task = await this.getTask(taskId)

    if (!task) {
      console.error('[SupabaseProvider] Task not found:', taskId)
      alert('Task hittades inte!')
      return
    }

    console.log('[SupabaseProvider] Task found:', {
      id: task.id,
      name: task.name,
      source: task.source,
      sourceUrl: task.sourceUrl,
      hasSourceUrl: !!task.sourceUrl
    })

    if (task.sourceUrl) {
      // Check if this is a fallback search URL (Obsidian or Roam)
      if ((task.source === 'obsidian' || task.source === 'roam') && task.sourceUrl.includes('search')) {
        const sourceName = task.source === 'obsidian' ? 'Obsidian' : 'Roam'
        const confirmOpen = confirm(
          `⚠️ Detta är en fallback-URL som öppnar ${sourceName}-sökning.\n\n` +
          `För att öppna rätt ${task.source === 'obsidian' ? 'note' : 'page'} behöver du synca om dina tasks med korrekt source_url:\n` +
          `${task.source === 'obsidian' ? 'Format: obsidian://open?vault={vault}&file={file}' : 'Format: https://roamresearch.com/#/app/{graph}/page/{uid}'}\n\n` +
          'Vill du öppna sökningen ändå?'
        )

        if (!confirmOpen) {
          return
        }
      }

      console.log('[SupabaseProvider] Opening sourceUrl:', task.sourceUrl)
      openExternalUrl(task.sourceUrl)
    } else {
      console.warn('[SupabaseProvider] No sourceUrl found for task:', task.name, 'Task object:', task)
      alert(`Denna task har ingen länk till källan.\n\nTask: "${task.name}"\nSource: ${task.source}\n\nOm du synkar tasks från ${task.source}, se till att source_url är satt i databasen.`)
    }
  }

  async getActiveSources(): Promise<string[]> {
    const { data } = await supabase
      .from('tasks')
      .select('source')
      .eq('user_id', this.userId)

    if (!data) return []

    const sources = new Set(data.map(row => row.source))
    return Array.from(sources)
  }

  async getSetting<T>(key: string): Promise<T | null> {
    try {
      // Try to get from Supabase first
      const { data, error } = await supabase
        .from('user_settings')
        .select('value')
        .eq('user_id', this.userId)
        .eq('key', key)
        .maybeSingle()

      if (error) {
        console.error('[SupabaseProvider] Error fetching setting:', error)
        // Fallback to localStorage - use same key format as settings-storage.ts
        const storageKey = key === 'task_sidekick_settings'
          ? `taskSidekickSettings:${this.userId}`
          : `task-sidekick:${key}`
        const stored = localStorage.getItem(storageKey)
        return stored ? JSON.parse(stored) : null
      }

      if (data?.value) {
        return data.value as T
      }

      // If not in database, check localStorage for migration
      // Use same key format as settings-storage.ts
      const storageKey = key === 'task_sidekick_settings'
        ? `taskSidekickSettings:${this.userId}`
        : `task-sidekick:${key}`
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const value = JSON.parse(stored) as T
        // Migrate to database
        await this.setSetting(key, value)
        return value
      }

      return null
    } catch (error) {
      console.error('[SupabaseProvider] Exception in getSetting:', error)
      return null
    }
  }

  async setSetting<T>(key: string, value: T): Promise<void> {
    try {
      // Save to Supabase with upsert (insert or update)
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: this.userId,
          key,
          value: value as any,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,key'
        })

      if (error) {
        console.error('[SupabaseProvider] Error saving setting:', error)
        // Fallback to localStorage
        localStorage.setItem(`task-sidekick:${key}`, JSON.stringify(value))
        throw error
      }

      // Also keep in localStorage as cache/backup
      localStorage.setItem(`task-sidekick:${key}`, JSON.stringify(value))
    } catch (error) {
      console.error('[SupabaseProvider] Exception in setSetting:', error)
      // Ensure it's at least saved to localStorage
      localStorage.setItem(`task-sidekick:${key}`, JSON.stringify(value))
      throw error
    }
  }

  private transformTask(row: any): Task {
    return {
      id: row.id,
      name: row.content || row.name, // nodes table uses 'content', old tasks table uses 'name'
      rawName: row.rawName || row.raw_name,
      note: row.note,
      dueDate: row.dueDate || row.due_date,
      dueTime: row.dueTime || row.due_time,
      duration: row.duration,
      completed: row.completed,
      status: row.status || 'todo',
      priority: row.priority || 0,
      tags: row.tags || [],
      repeat: row.repeat,
      repeatId: row.repeatId || row.repeat_id,
      project: row.project,
      projectPath: row.projectPath || row.project_path,
      source: row.source,
      url: row.url,
      sourceUrl: row.sourceUrl || row.source_url,
      assignedTo: row.assignedTo || row.assigned_to || 'user',
      isCalendarEvent: row.isCalendarEvent || row.is_calendar_event || false,
      calendarId: row.calendarId || row.calendar_id,
      eventId: row.eventId || row.event_id,
      calendarName: row.calendarName || row.calendar_name,
      reminders: row.reminders || [],
      reminderMinutes: row.reminderMinutes || row.reminder_minutes,
      attachments: row.attachments || [],
    }
  }
}
